import { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './styles.css';
import { GeometryItem, MapProps } from './type';
import { useTranslation, TranslationKey } from '@/hooks/useTranslation';
import bbox from '@turf/bbox';
import { getCachedGeoJSON } from '@/utils/functions/geojsonCache';

const NEIGHBOR_LINE_COLOR = '#b0b0b0';
const NEIGHBOR_LINE_WIDTH = 0.75;

export const StaticMap = ({
  geometryCoordinates,
  center = [-51.9253, -14.235],
  mapColor,
  zoom = 3.6,
  variant,
  onViewportChange,
  neighboringGeometries = [],
  onNeighborClick,
}: MapProps) => {
  const { t } = useTranslation();
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const isStyleLoadedRef = useRef<boolean>(false);
  const onNeighborClickRef = useRef(onNeighborClick);
  onNeighborClickRef.current = onNeighborClick;

  const handleMapColor = () => {
    if (variant === 'risk') {
      return {
        fillColor: 'intensity_color_risk',
        lineColor: '#513309',
      };
    }
    return {
      fillColor: 'intensity_color_opportunity',
      lineColor: '#072250',
    };
  };

  const translateRegion = (regionName: string) => {
    if (!regionName) return '';
    const normalizedRegion = regionName.toLowerCase().trim();
    const translationKey = `page.municipality.municipality.region.${normalizedRegion}` as TranslationKey;
    return t(translationKey);
  };

  const getGeometryData = () => {
    if (geometryCoordinates) {
      return [
        {
          coordinates: geometryCoordinates,
          name: 'Município desconhecido',
          municipality_code: '',
          code_uf: '',
          name_uf: '',
          biome: '',
          population: 0,
          classification_type: '',
          name_region: '',
          municipality_score: {
            general_score_risk: 0,
            general_score_opportunity: 0,
            intensity_color_risk: '',
            intensity_color_opportunity: '',
            classification_risk: '',
            classification_opportunity: '',
          },
        },
      ];
    }
    return [];
  };

  const updateNeighborsOnMap = (geometryItems: GeometryItem[]) => {
    if (!mapRef.current || !isStyleLoadedRef.current) return;
    const map = mapRef.current;

    const features = geometryItems.map((item) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'MultiPolygon' as const,
        coordinates: item.coordinates,
      },
      properties: {
        municipality_code: item.municipality_code ?? '',
      },
    }));

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    if (map.getSource('trri-neighbors')) {
      const source = map.getSource('trri-neighbors') as mapboxgl.GeoJSONSource;
      source.setData(geojson);
    } else {
      map.addSource('trri-neighbors', {
        type: 'geojson',
        data: geojson,
      });
      map.addLayer({
        id: 'trri-neighbors-fill',
        type: 'fill',
        source: 'trri-neighbors',
        paint: {
          'fill-color': '#888',
          'fill-opacity': 0,
        },
      });
      map.addLayer({
        id: 'trri-neighbors-outline',
        type: 'line',
        source: 'trri-neighbors',
        paint: {
          'line-color': NEIGHBOR_LINE_COLOR,
          'line-width': NEIGHBOR_LINE_WIDTH,
        },
      });
    }
  };

  const updateGeometryOnMap = (geometryItems: GeometryItem[]) => {
    if (!mapRef.current || !isStyleLoadedRef.current) return;
    const map = mapRef.current;

    const features = geometryItems.map((item) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'MultiPolygon' as const,
        coordinates: item.coordinates,
      },
      properties: {
        name: item.name,
        municipality_code: item.municipality_code,
        code_uf: item.code_uf,
        name_uf: item.name_uf,
        biome: item.biome,
        population: item.population,
        classification_type: item.classification_type,
        name_region: translateRegion(item.name_region || ''),
        municipality_score: item.municipality_score,
      },
    }));

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    if (map.getSource('trri-multipolygon')) {
      const source = map.getSource('trri-multipolygon') as mapboxgl.GeoJSONSource;
      source.setData(geojson);
    } else {
      map.addSource('trri-multipolygon', {
        type: 'geojson',
        data: geojson,
      });

      map.addLayer({
        id: 'trri-multipolygon-fill',
        type: 'fill',
        source: 'trri-multipolygon',
        paint: {
          'fill-color': mapColor?.fillColor ?? [
            'get',
            handleMapColor().fillColor,
            ['get', 'municipality_score'],
          ],
          'fill-opacity': 1,
        },
      });

      map.addLayer({
        id: 'trri-multipolygon-outline',
        type: 'line',
        source: 'trri-multipolygon',
        paint: {
          'line-color': mapColor?.lineColor ?? handleMapColor().lineColor,
          'line-width': 1,
        },
      });
    }

    if (features.length > 0) {
      const bounds = bbox(geojson);
      map.fitBounds(bounds as [number, number, number, number], {
        padding: 40,
        maxZoom: 10,
        duration: 800,
      });
    }
  };

  useEffect(() => {
    if (!mapRef.current && mapContainerRef.current) {
      mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

      const map = new mapboxgl.Map({
        style: 'mapbox://styles/mapbox/standard',
        container: mapContainerRef.current,
        center,
        zoom,
        minZoom: 3.5,
      });

      mapRef.current = map;
      map.setProjection('mercator');

      map.on('style.load', async () => {
        isStyleLoadedRef.current = true;

        map.setConfigProperty('basemap', 'showRoadLabels', false);
        map.setConfigProperty('basemap', 'showPointOfInterestLabels', false);
        map.setConfigProperty('basemap', 'showTransitLabels', false);

        const brazilGeojson = await getCachedGeoJSON('/geojson/brazil.geojson');
        const brazilGeometry = brazilGeojson.features[0]?.geometry;
        const brazilHoleCoords =
          brazilGeometry?.type === 'Polygon'
            ? brazilGeometry.coordinates
            : brazilGeometry?.type === 'MultiPolygon'
              ? brazilGeometry.coordinates.flat()
              : [];

        const worldWithHole = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [
                  [
                    [-180, -90],
                    [180, -90],
                    [180, 90],
                    [-180, 90],
                    [-180, -90],
                  ],
                  ...brazilHoleCoords,
                ],
              },
            },
          ],
        };

        map.addSource('world-mask', {
          type: 'geojson',
          data: worldWithHole as GeoJSON.FeatureCollection,
        });

        map.addLayer({
          id: 'world-mask-fill',
          type: 'fill',
          source: 'world-mask',
          paint: {
            'fill-color': 'rgba(0,0,0,0.8)',
          },
        });

        map.addSource('trri-neighbors', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        map.addLayer({
          id: 'trri-neighbors-fill',
          type: 'fill',
          source: 'trri-neighbors',
          paint: {
            'fill-color': '#888',
            'fill-opacity': 0,
          },
        });
        map.addLayer({
          id: 'trri-neighbors-outline',
          type: 'line',
          source: 'trri-neighbors',
          paint: {
            'line-color': NEIGHBOR_LINE_COLOR,
            'line-width': NEIGHBOR_LINE_WIDTH,
          },
        });

        if (onNeighborClick) {
          const handleNeighborClick = (e: mapboxgl.MapMouseEvent) => {
            const features = map.queryRenderedFeatures(e.point, {
              layers: ['trri-neighbors-fill'],
            });
            const code = features[0]?.properties?.municipality_code;
            if (typeof code === 'string' && code) {
              const { clientX, clientY } = e.originalEvent;
              onNeighborClickRef.current?.(code, { x: clientX, y: clientY });
            }
          };
          map.on('click', 'trri-neighbors-fill', handleNeighborClick);
          map.on('mouseenter', 'trri-neighbors-fill', () => {
            map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', 'trri-neighbors-fill', () => {
            map.getCanvas().style.cursor = '';
          });
        }

        updateGeometryOnMap(getGeometryData());
        updateNeighborsOnMap(neighboringGeometries);

        if (onViewportChange) {
          const reportBounds = () => {
            if (!mapRef.current) return;
            const b = mapRef.current.getBounds();
            if (!b) return;
            onViewportChange([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
          };
          map.on('moveend', reportBounds);
          setTimeout(reportBounds, 900);
        }
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        isStyleLoadedRef.current = false;
      }
    };
  }, [zoom]);

  useEffect(() => {
    if (mapRef.current) {
      updateGeometryOnMap(getGeometryData());
    }
  }, [geometryCoordinates]);

  useEffect(() => {
    if (mapRef.current && isStyleLoadedRef.current) {
      updateNeighborsOnMap(neighboringGeometries);
    }
  }, [neighboringGeometries]);

  return <div id="map-container" ref={mapContainerRef} />;
};
